import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

/// Active-customer / active-group state for the web app.
///
/// `customerId === null` means consolidated mode — the user sees data
/// from every customer they have access to (the "All" choice in the
/// switcher). A UUID means the user has narrowed to that specific
/// customer; the api-client interceptor will attach it as
/// `X-Customer-Id` on every request.
///
/// `groupScope` is a SEPARATE field, deliberately not merged into
/// `customerId` — mirrors the backend's `request.customerContext` vs
/// `request.groupScope` split (see api/src/plugins/customer-context.js).
/// Every existing place that reads `customerId` as "a single real
/// customer id or null" keeps working unchanged; a Group/Sub Group
/// selection is exclusively represented by `groupScope` instead. The
/// two are mutually exclusive — picking one always clears the other,
/// since the switcher UI presents both in one tree but only one node
/// is ever "active" at a time.
///
/// Persisted in localStorage so a refresh doesn't drop the user back
/// to "All". When the user signs out, the auth store clears it.
export type GroupScope = { kind: 'group' | 'subgroup'; id: string; name: string } | null

interface CustomerState {
  customerId: string | null
  groupScope: GroupScope
  setCustomer: (id: string | null) => void
  setGroupScope: (scope: GroupScope) => void
  clear: () => void
}

export const useCustomerStore = create<CustomerState>()(
  persist(
    (set) => ({
      customerId: null,
      groupScope: null,
      setCustomer: (id) => set({ customerId: id, groupScope: null }),
      setGroupScope: (scope) => set({ groupScope: scope, customerId: null }),
      clear: () => set({ customerId: null, groupScope: null }),
    }),
    {
      name: 'cmms-active-customer',
      storage: createJSONStorage(() => localStorage),
    },
  ),
)
